import { createAsyncThunk } from '@reduxjs/toolkit';
import { psoLogsService as service } from 'services/serviceStorage';
import { wrapRequest } from 'common/utils';
import { BaseApiResponse } from 'common/types';
import { Scenario, ScenarioFilter, GetFileComparisonResponse, GetWarningsTypesResponse, PSOLogScenario } from './types';
import { mapComparedWarningsTypes, mapWarningsTypes, mapPSOLogsScenarioItem } from './helpers';

export const fetchScenarios = createAsyncThunk<{ data: Scenario[]; total: number; filter: ScenarioFilter }, ScenarioFilter>(
    'psoLogs/fetchScenarios',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: PSOLogScenario[]; totalCount: number }>>({
            handler: () => service.getScenarios(filter),
            showNotifications: true,
        });

        return {
            data: data!.result.items.map(mapPSOLogsScenarioItem),
            total: data!.result.totalCount,
            filter,
        };
    });

export const loadScenarios = createAsyncThunk<void, number[]>(
    'psoLogs/loadScenarios', async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.loadScenarios(scenarioIds),
            showNotifications: true,
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    });

export const getWarningsTypes = createAsyncThunk<GetWarningsTypesResponse, number>(
    'psoLogs/getWarningsTypes',
    async (scenarioId) => {
        const { data } = await wrapRequest<BaseApiResponse<{ getWarningsTypes: { type: string; value: string } }>>({
            handler: () => service.getDashboard({ tables: ['getWarningsTypes'], filters: { scenarioId } }),
            showNotifications: true,
        });

        return mapWarningsTypes(data?.result!, scenarioId)!;
    });

export const getFileComparison = createAsyncThunk<GetFileComparisonResponse, { scenarioId: number; targetId: number; }>(
    'psoLogs/getFileComparison',
    async ({ scenarioId, targetId }) => {
        const { data } = await wrapRequest<BaseApiResponse<{ compareWarningsTypes: { type: string; value: string } }>>({
            handler: () => service.getDashboard({ tables: ['compareWarningsTypes'], filters: { scenarioId, targetId } }),
            showNotifications: true,
        });

        return mapComparedWarningsTypes(data?.result!)!;
    });

export const dumpScenarioLogs = createAsyncThunk<string, { logLevels: string[], compareRules: string[], scenarioIds: number[] }>(
    'psoLogs/dumpScenarioLogs',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => service.dumpScenarioLogs(filter),
            showNotifications: true,
        });

        return data?.result!;
    });